import type { TravelBooking } from './queries';
import {
  BookingActionState,
  canConfirmBooking,
  canSyncActiveOperation,
  isOnRequest,
} from './bookingState';
import { isReadableExpiry } from './holdCountdown';
import {
  BookingRecoveryCardKind,
  BookingRecoveryView,
  bookingRecoveryCardKind,
  canSyncBookingRecovery,
} from './bookingRecovery';

/**
 * Lo que cada FILA del listado decide por su cuenta: qué acción ofrece y qué
 * hito viene después.
 *
 * Las dos salen de los predicados que ya gobiernan el modal —`bookingState` y
 * `bookingRecovery`—, no de una lectura propia del estado: la fila y el detalle
 * tienen que ofrecer lo mismo sobre la misma reserva, y una segunda matriz de
 * estados sería exactamente cómo empiezan a discrepar (§21.22).
 */

/** Lo mínimo de una reserva para decidir su fila. */
export type BookingListRowView = BookingActionState &
  BookingRecoveryView & {
    source?: string;
    ruteroBookingId?: string;
    providerStatus?: string;
    expiresAt?: string;
    ticketsReady?: boolean;
  };

export type BookingListActionKind = 'confirm' | 'sync' | 'manual' | 'view';

export interface BookingListAction {
  kind: BookingListActionKind;
  labelKey: string;
  /**
   * `sync` es la única que se ejecuta desde la fila: es una sola llamada sin
   * nada que decidir, y es LA acción de una cola de trabajo. Confirmar,
   * resolver a mano y mirar abren el detalle, donde están la plata, las
   * preguntas del operador y la tarjeta de recuperación con su explicación.
   */
  opensDetail: boolean;
}

const ACTIONS: Record<BookingListActionKind, BookingListAction> = {
  confirm: {
    kind: 'confirm',
    labelKey: 'travelServices.booking.confirm',
    opensDetail: true,
  },
  sync: {
    kind: 'sync',
    labelKey: 'travelServices.booking.sync',
    opensDetail: false,
  },
  manual: {
    kind: 'manual',
    labelKey: 'travelServices.bookingList.actions.resolve',
    opensDetail: true,
  },
  view: {
    kind: 'view',
    labelKey: 'travelServices.bookingList.actions.view',
    opensDetail: true,
  },
};

/**
 * ¿Se puede operar con el proveedor? Sin id remoto no hay nada que confirmar
 * ni que sincronizar — es la misma condición que el detalle usa para ofrecer
 * sus botones.
 */
function canOperate(booking: BookingListRowView): boolean {
  return Boolean(booking.ruteroBookingId);
}

/**
 * LA acción de la fila, una sola. El orden de las ramas es el de la urgencia:
 * la reserva que se puede cerrar hoy, la que quedó sin identidad y hay que
 * conciliar, la que se destraba con un sync, y mirar para todo lo demás.
 *
 * `manual` va ANTES que `sync` aunque sean excluyentes por construcción (la
 * conciliación manual existe justamente porque no hay id remoto): que el orden
 * lo garantice cuesta nada y no depende de que esa invariante siga siendo
 * cierta.
 */
export function bookingListAction(booking: BookingListRowView): BookingListAction {
  if (booking.source === 'hotelrs') return ACTIONS.view;
  if (canOperate(booking) && canConfirmBooking(booking)) return ACTIONS.confirm;
  if (bookingRecoveryCardKind(booking) === 'manual') return ACTIONS.manual;
  if (
    canOperate(booking) &&
    booking.activeOperationStatus !== 'in_progress' &&
    (canSyncBookingRecovery(booking) ||
      isOnRequest(booking.providerStatus) ||
      canSyncActiveOperation(booking))
  ) {
    return ACTIONS.sync;
  }
  return ACTIONS.view;
}

/**
 * El PRÓXIMO HITO: una sola columna que dice lo que importa según el estado,
 * en vez de dos columnas de las que una está siempre vacía.
 *
 * - `holdCountdown`: el reloj del proveedor, que es lo único que se pierde
 *   solo. Lo dibuja el mismo contador que la barra del modal.
 * - `recovery`: la reserva no convergió; el texto nombra el paso que queda.
 * - `awaitingProvider` / `tickets*`: ya confirmada, qué falta para viajar.
 */
export type BookingListMilestone =
  | { kind: 'holdCountdown' }
  | { kind: 'recovery'; recovery: BookingRecoveryCardKind; textKey: string }
  | { kind: 'awaitingProvider'; textKey: string }
  | { kind: 'ticketsReady'; textKey: string }
  | { kind: 'ticketsPending'; textKey: string }
  | { kind: 'none' };

/**
 * El paso que queda por cada cara de la recuperación. Es un `Record` sobre las
 * caras que ya define `bookingRecovery`: una cara nueva no compila hasta decir
 * qué sigue, y no hay acá ningún estado que el modal no conozca.
 */
const RECOVERY_NEXT_STEP: Record<BookingRecoveryCardKind, string> = {
  transportAmbiguous: 'travelServices.bookingList.nextStep.transportAmbiguous',
  unknownStateSync: 'travelServices.bookingList.nextStep.unknownStateSync',
  manual: 'travelServices.bookingList.nextStep.manual',
  adoptionPending: 'travelServices.bookingList.nextStep.adoptionPending',
};

export function bookingListMilestone(
  booking: BookingListRowView,
): BookingListMilestone {
  const recovery = bookingRecoveryCardKind(booking);
  if (recovery) {
    return { kind: 'recovery', recovery, textKey: RECOVERY_NEXT_STEP[recovery] };
  }
  // La MISMA condición con la que el reloj se dibuja: prometer contador y no
  // tener nada que contar deja la celda muda.
  if (booking.state === 'held' && isReadableExpiry(booking.expiresAt)) {
    return { kind: 'holdCountdown' };
  }
  if (booking.state === 'confirmed') {
    if (isOnRequest(booking.providerStatus)) {
      return {
        kind: 'awaitingProvider',
        textKey: 'travelServices.bookingList.nextStep.awaitingProvider',
      };
    }
    return booking.ticketsReady
      ? {
        kind: 'ticketsReady',
        textKey: 'travelServices.bookingList.nextStep.ticketsReady',
      }
      : {
        kind: 'ticketsPending',
        textKey: 'travelServices.bookingList.nextStep.ticketsPending',
      };
  }
  return { kind: 'none' };
}
/** Keep hotel identity and confirmed decimal amounts separate from Rutero pricing. */
export function bookingListServiceName(
  booking: Pick<TravelBooking, 'source' | 'hotelSnapshot' | 'fareSnapshot' | 'vertical'>,
  translate: (key: string) => string,
): string {
  if (booking.source === 'hotelrs') {
    return booking.hotelSnapshot?.propertyName?.trim() || translate('hotelSearch.title');
  }
  return booking.fareSnapshot?.name ?? booking.fareSnapshot?.title ?? translate(`rutero.verticals.${booking.vertical}`);
}

export function bookingListHotelTotal(booking: Pick<TravelBooking, 'source' | 'state' | 'hotelSnapshot'>): string {
  const snapshot = booking.hotelSnapshot;
  const price = snapshot?.confirmedPrice;
  if (booking.source !== 'hotelrs' || booking.state !== 'confirmed' || snapshot?.phase !== 'confirmed' || !price)
    return '—';
  return `${price.currency} ${price.amount}`;
}
